import { Invitation } from 'models/invitations';
import { useMutation, useQuery } from 'react-query';
import {
  acceptInvitation,
  createInvitation,
  declineInvitation,
  getInvitationById,
  listBusinessInvitationsByBusinessId,
  listInvitations,
  resendInvitation,
  updateInvitation,
  voidInvitation,
} from 'services/invitations';
import { queryClient } from 'states/reactQueryClient';

export const useInvitations = () => {
  const { data, ...rest } = useQuery({
    queryKey: ['invitations'],
    queryFn: () => listInvitations(),
  });

  return {
    invitations: data,
    ...rest,
  };
};

export const useInvitationById = (id: string) => {
  const { data, ...rest } = useQuery({
    queryKey: ['invitations', id],
    queryFn: () => getInvitationById(id),
  });

  return {
    invitation: data,
    ...rest,
  };
};

export const useBusinessInvitations = (businessId: string) => {
  const { data, ...rest } = useQuery({
    queryKey: ['invitations', businessId],
    queryFn: () => listBusinessInvitationsByBusinessId(businessId),
    enabled: !!businessId,
  });

  return {
    invitations: data,
    ...rest,
  };
};

export const useCreateInvitation = () => {
  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: (invitation: Partial<Invitation>) =>
      createInvitation(invitation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  return {
    createInvitation: mutate,
    createInvitationAsync: mutateAsync,
    ...rest,
  };
};

export const useUpdateInvitation = () => {
  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: ({ id, invitation }: { id: string; invitation: Invitation }) =>
      updateInvitation(id, invitation),
  });

  return {
    updateInvitation: mutate,
    updateInvitationAsync: mutateAsync,
    ...rest,
  };
};

export const useResendInvitation = () => {
  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: (id: string) => resendInvitation(id),
  });

  return {
    resendInvitation: mutate,
    resendInvitationAsync: mutateAsync,
    ...rest,
  };
};

export const useAcceptInvitation = () => {
  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: (id: string) => acceptInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  return {
    acceptInvitation: mutate,
    acceptInvitationAsync: mutateAsync,
    ...rest,
  };
};

export const useDeclineInvitation = () => {
  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: (id: string) => declineInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  return {
    declineInvitation: mutate,
    declineInvitationAsync: mutateAsync,
    ...rest,
  };
};

export const useVoidInvitation = () => {
  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: (id: string) => voidInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  return {
    voidInvitation: mutate,
    voidInvitationAsync: mutateAsync,
    ...rest,
  };
};
